import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { includes } from 'lodash';
import { lighten } from '@mui/material/styles';
import { makeStyles } from 'src/makeStyles';
import MuiButton from '@mui/material/Button';

import Loader from 'components/Loader';
import { borderRadius, colors } from '../../constants/styleConstants';
import { BUTTON_VARIANTS } from '../../constants/consts';

import ButtonV1 from './ButtonV1';

const useStyles = makeStyles()(theme => ({
  button: {
    borderRadius: borderRadius.button,
    padding: '0.73rem',
    fontWeight: 'bold',
    minWidth: 165,
    cursor: 'pointer',
    textTransform: 'none',
    '&:active': {
      opacity: 0.6,
    },
  },
  contained: {
    boxShadow: 'none',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  containedPrimary: {
    '&:hover': {
      backgroundColor: lighten(theme.palette.primary.main, 0.3),
    },
  },
  outlined: {
    backgroundColor: colors.white,
  },
}));

const Button = ({ children, className, styles, variant, color, isLoading, disabled, ...rest }) => {
  const { classes, cx } = useStyles({ variant });

  if (includes(['primary', 'secondary', 'tertiary'], variant)) {
    return (
      <ButtonV1 variant={variant} className={className} isLoading={isLoading} disabled={disabled} {...rest}>
        {children}
      </ButtonV1>
    );
  }

  const styleProps = {
    className: cx(classes.button, className),
    classes: {
      contained: classes.contained,
      containedPrimary: classes.containedPrimary,
      outlined: classes.outlined,
    },
  };

  return (
    <MuiButton
      style={styles}
      color={color}
      variant={variant}
      disabled={disabled || isLoading}
      {...styleProps}
      {...rest}
    >
      {isLoading && <Loader fastAnimation small color="white" />}
      {!isLoading && children}
    </MuiButton>
  );
};

Button.propTypes = {
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string, PropTypes.node]),
  className: PropTypes.string,
  variant: PropTypes.oneOf(['contained', 'outlined', 'primary', 'secondary', 'tertiary']),
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};

Button.defaultProps = {
  variant: BUTTON_VARIANTS.CONTAINED,
  color: 'primary',
  isLoading: false,
  disabled: false,
};

export default memo(Button);
